import React from 'react';
import { Info, Dice1, BarChart2, Shield, Ticket, Lightbulb, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о случайных числах и их применении
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о случайных числах: теория, применение, безопасность
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Dice1 className="w-5 h-5 text-indigo-500" />
          Что такое случайные числа?
        </h3>
        <p>
          <strong>Случайные числа</strong> — это последовательность чисел, каждое из которых непредсказуемо и не зависит от предыдущих значений. В отличие от обычных алгоритмических последовательностей, истинно случайные числа невозможно предсказать, даже зная алгоритм и предыдущие значения.
        </p>
        <p>
          В реальном мире мы различаем два типа случайных чисел:
        </p>
        <ul>
          <li><strong>Истинно случайные числа</strong> — получаются из физических источников случайности (квантовые явления, радиоактивный распад, атмосферный шум и т.д.)</li>
          <li><strong>Псевдослучайные числа</strong> — генерируются детерминированными алгоритмами, которые создают последовательности, обладающие статистическими свойствами случайности</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-indigo-500" />
          Типы распределений случайных чисел
        </h3>
        <p>
          Случайные числа могут подчиняться различным распределениям вероятностей:
        </p>
        <ul>
          <li><strong>Равномерное распределение</strong> — все значения в диапазоне имеют одинаковую вероятность появления. Это наиболее часто используемое распределение для генерации случайных чисел.</li>
          <li><strong>Нормальное (гауссово) распределение</strong> — большинство сгенерированных значений группируются около среднего значения, а крайние значения встречаются реже. Это распределение широко распространено в природе и часто используется в статистике.</li>
          <li><strong>Взвешенное распределение</strong> — вероятность появления разных значений задается вручную. Используется, когда нужно смоделировать неравномерные вероятности (например, выигрыши в азартных играх).</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-indigo-500" />
          Генераторы случайных чисел в России
        </h3>
        <p>
          В России генераторы случайных чисел активно используются в различных областях:
        </p>
        <ul>
          <li>
            <strong>Государственные лотереи</strong> — «Гослото», «Русское лото» и другие официальные лотереи используют сертифицированные генераторы случайных чисел, прошедшие проверку в ФНС России.
          </li>
          <li>
            <strong>Криптография и защита информации</strong> — российские стандарты шифрования (ГОСТ) используют криптостойкие генераторы случайных чисел.
          </li>
          <li>
            <strong>Научные исследования</strong> — от моделирования физических процессов до социологических исследований.
          </li>
          <li>
            <strong>Государственные процедуры</strong> — распределение квот, выбор присяжных заседателей, жеребьевки при проведении конкурсов на государственные должности.
          </li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2">
            <Ticket className="w-4 h-4" />
            Интересный факт о лотереях в России
          </h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            В России при проведении лотерей «Гослото» используются два независимых генератора случайных чисел — основной и контрольный. Результаты обоих генераторов должны совпадать для подтверждения честности розыгрыша. Это повышает доверие к результатам и минимизирует риск манипуляций.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-indigo-500" />
          Популярные применения генераторов случайных чисел
        </h3>
        <ul>
          <li>
            <strong>Лотереи и азартные игры</strong> — для честного определения победителей и результатов игр.
          </li>
          <li>
            <strong>Научные исследования и статистика</strong> — для моделирования экспериментов и обеспечения репрезентативных выборок.
          </li>
          <li>
            <strong>Криптография</strong> — для создания криптографических ключей и защиты данных.
          </li>
          <li>
            <strong>Компьютерные игры</strong> — для процедурной генерации контента и непредсказуемого поведения игровых персонажей.
          </li>
          <li>
            <strong>Моделирование и симуляции</strong> — для создания реалистичных моделей сложных систем (от погоды до финансовых рынков).
          </li>
          <li>
            <strong>Образование</strong> — для создания учебных задач, тестов и случайного выбора учеников при опросе.
          </li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Важно знать о генераторах случайных чисел
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Компьютерные генераторы создают псевдослучайные числа, которые могут выглядеть случайными, но теоретически предсказуемы</li>
            <li>При использовании одинакового seed (начального значения) генератор всегда создаст одинаковую последовательность чисел</li>
            <li>Для критически важных применений (криптография, безопасность) следует использовать аппаратные генераторы истинно случайных чисел</li>
            <li>Некоторые генераторы могут иметь статистические смещения, влияющие на качество случайности</li>
          </ul>
        </div>

        <h3>История случайных чисел в вычислительной технике</h3>
        <p>
          В СССР первые генераторы псевдослучайных чисел были разработаны в 1950-х годах для решения задач методом Монте-Карло — статистического метода численного решения математических задач. Советские ученые активно использовали генераторы случайных чисел при создании ядерного оружия, расчетах траекторий космических аппаратов и моделировании экономических процессов.
        </p>
        <p>
          В современной России научная школа случайных процессов продолжает развиваться. Российскими учеными разработаны оригинальные алгоритмы генерации псевдослучайных чисел, которые используются в отечественном программном обеспечении, системах защиты информации и научных исследованиях.
        </p>

        <h3>Проверка качества случайных чисел</h3>
        <p>
          Для оценки качества генераторов случайных чисел используются специальные статистические тесты:
        </p>
        <ul>
          <li>Частотный тест — проверяет равномерность распределения цифр</li>
          <li>Сериальный тест — анализирует последовательности цифр</li>
          <li>Тест на монотонность — проверяет отсутствие трендов</li>
          <li>Спектральный тест — анализирует спектральные характеристики последовательности</li>
          <li>Тест на сжимаемость — проверяет, насколько сжимаема последовательность</li>
        </ul>
        <p>
          В России для проверки генераторов случайных чисел применяются как международные наборы тестов (например, NIST STS), так и отечественные методики, разработанные ФСБ России для сертификации средств криптографической защиты информации.
        </p>

        <p className="font-semibold mt-6">
          Наш генератор случайных чисел предлагает широкие возможности настройки и позволяет получать последовательности с разными типами распределений для различных задач — от простых лотерей до сложного статистического моделирования!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;